import { useCallback, useEffect, useMemo, useState } from "react";
import { request } from "../../../../utils/api";

import { formatPatientChartName } from "../../../Patients/components/clinicalForms/dailyLogEval";

const CACHE_TTL_MS = 30_000;
let cache = { loadedAt: 0, patients: [] };

export const OTHER_PATIENT_VALUE = "other";

export const patientDisplayName = (patient) =>
  formatPatientChartName(patient);

const toPickerPatient = (row) => ({
  id: Number(row.id),
  firstName: row.firstName || "",
  middleName: row.middleName || "",
  lastName: row.lastName || "",
  mrn: row.mrn || "",
  dateOfBirth: row.dateOfBirth || null,
  name: formatPatientChartName(row),
});

const parsePatientList = (response) => {
  const rows = response?.result?.patients || response?.patients || [];
  return Array.isArray(rows)
    ? rows.map(toPickerPatient).filter((patient) => patient.id)
    : [];
};

/**
 * Patients the current user may link on a Daily Log.
 * Admin/OA: all active patients. Others: assigned caseload.
 */
export const useDailyLogPatients = (enabled = true) => {
  const [patients, setPatients] = useState(cache.patients);
  const [loading, setLoading] = useState(
    enabled && Date.now() - cache.loadedAt > CACHE_TTL_MS,
  );

  const load = useCallback(
    async (force = false) => {
      if (!enabled) return;
      const fresh = Date.now() - cache.loadedAt < CACHE_TTL_MS;
      if (!force && fresh && cache.patients.length) {
        setPatients(cache.patients);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await request("/daily-logs/patients");
        const list = parsePatientList(response);
        cache = { loadedAt: Date.now(), patients: list };
        setPatients(list);
      } catch {
        setPatients(cache.patients);
      } finally {
        setLoading(false);
      }
    },
    [enabled],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return undefined;
  }, [enabled, load]);

  const byId = useMemo(() => {
    const map = new Map();
    patients.forEach((patient) => map.set(Number(patient.id), patient));
    return map;
  }, [patients]);

  return { patients, loading, byId, reload: () => load(true) };
};

export const invalidateDailyLogPatientsCache = () => {
  cache = { loadedAt: 0, patients: [] };
};
